import { createSelector } from 'reselect';

import { getEventCenterConfig } from '../../constants/eventCenter';
import { __ } from '../../i18n/l10n';
import { getMatchIdFromProps } from '../../selectors/_propsGetters_';
import {
  selectEntitiesMatchesEntityCategoryId,
  selectEntitiesMatchesEntityHighlights,
  selectEntitiesMatchesEntityImgEventId,
  selectEntitiesMatchesEntityImgFightId,
  selectEntitiesMatchesEntityIsOutright,
  selectEntitiesMatchesEntityIsPrivateScore,
  selectEntitiesMatchesEntityParentStageId,
  selectEntitiesMatchesEntityPeriodId,
  selectEntitiesMatchesEntitySportId,
  selectEntitiesMatchesEntityStream,
  selectEntitiesMatchesEntityTournamentId,
} from '../../selectors/entities/matches/entitiesMatchesPrimary.selector';
import {
  selectIsMatchSportLineUpCompatible,
  selectMatchHasTracker,
  selectMatchTournamentsEntityDisplayCupRoster,
  selectMatchTournamentsEntityDisplayLiveTable,
  selectMatchTournamentsEntitySrSeasonId,
  selectMatchTournamentsEntitySrTournamentId,
  selectMatchViewMode,
  selectMatchHasHighlights,
  selectMatchHasStream,
  selectFirstMatchIdInMatchTournament,
  selectIsMatchLive,
  selectIsMatchEnded,
} from '../../selectors/match.selector';
import { selectRuntimeIsStreamHighlightsEnabled } from '../../selectors/runtime/runtimePrimary.selector';
import {
  selectUserConnected,
  selectUserConnecting,
  selectUserEmbeddedNotificationTarget,
  selectUserJwt,
} from '../../selectors/user/userPrimary.selector';
import { CategoryIds } from '../../types/categories';
import { Selector } from '../../types/selectors';
import { SportIds } from '../../types/sports';
import { TournamentIds } from '../../types/tournaments';
import { getBetRadarSeasonId, getBetRadarTournamentId } from '../../utils/betradar';
import MatchUtils from '../../utils/match';

import { BrWidgetPropsType, MatchToolbarWidgetOptionsType, WidgetWarningType } from './MatchToolbar';

const selectOptions = createSelector(
  [getMatchIdFromProps, selectEntitiesMatchesEntitySportId, selectEntitiesMatchesEntityParentStageId],
  (matchId, matchSportId, matchParentStageId): MatchToolbarWidgetOptionsType | null => {
    if (typeof matchSportId === 'number') {
      const options: MatchToolbarWidgetOptionsType = {
        scoreboard: 'disable',
        momentum: 'disable',
        tabsPosition: 'top',
        matchId,
      };

      if (matchSportId === SportIds.FOOTBALL) {
        // $FlowFixMe
        options.forceTeamInvert = false;
      }

      if (matchSportId === SportIds.FORMULA_1) {
        options.scoreboard = 'compact';
        // $FlowFixMe
        options.layout = 'single';
        options.momentum = 'compact';
        options.tabsPosition = 'top';
        // $FlowFixMe
        options.stageId = matchParentStageId;
      }

      if (matchSportId === SportIds.RUGBY_7S || matchSportId === SportIds.RUGBY_LEAGUE) {
        // $FlowFixMe
        options.sportId = SportIds.RUGBY;
      }

      return options;
    }

    return null;
  },
);

const selectDefaultTrackerTab = createSelector(selectEntitiesMatchesEntitySportId, (matchSportId) =>
  matchSportId === SportIds.FORMULA_1 ? 'track' : 'pitch',
);

const selectTrackerName = createSelector(selectEntitiesMatchesEntitySportId, (matchSportId) =>
  matchSportId === SportIds.FORMULA_1 ? 'match.lmtPlus.f1' : 'match.lmtPlus',
);

const selectShowWidget = createSelector(
  [selectEntitiesMatchesEntitySportId, selectEntitiesMatchesEntityParentStageId],
  (matchSportId, matchParentStageId) => {
    if (typeof matchSportId === 'number') {
      return matchSportId === SportIds.FORMULA_1 ? !!matchParentStageId : true;
    }

    return false;
  },
);

const selectDisplayLiveTable = createSelector(
  [
    selectEntitiesMatchesEntityIsOutright,
    selectMatchTournamentsEntityDisplayLiveTable,
    selectMatchTournamentsEntitySrTournamentId,
  ],
  (isOutright, displayLiveTable, srTournamentId) => !!(isOutright && displayLiveTable && srTournamentId),
);

const selectLiveTableProps: Selector<BrWidgetPropsType | null> = createSelector(
  [selectDisplayLiveTable, selectMatchTournamentsEntitySrTournamentId],
  (displayLiveTable, srTournamentId) =>
    displayLiveTable
      ? {
          brWidgetProps: {
            commandKey: 'season.liveTable',
            widgetOptions: {
              uniqueTournamentId: getBetRadarTournamentId(srTournamentId),
              disableWidgetHeader: true,
            },
          },
        }
      : null,
);

const selectDisplayCupRoster = createSelector(
  [
    selectEntitiesMatchesEntityIsOutright,
    selectMatchTournamentsEntityDisplayCupRoster,
    selectMatchTournamentsEntitySrSeasonId,
  ],
  (isOutright, displayCupRoster, srSeasonId) => !!(isOutright && displayCupRoster && srSeasonId),
);

const selectCupRosterProps: Selector<BrWidgetPropsType | null> = createSelector(
  [
    selectDisplayCupRoster,
    selectFirstMatchIdInMatchTournament,
    selectMatchTournamentsEntitySrSeasonId,
    selectMatchTournamentsEntitySrTournamentId,
  ],
  (displayCupRoster, firstMatchIdInTournament, srSeasonId, srTournamentId) =>
    displayCupRoster
      ? {
          brWidgetProps: {
            commandKey: 'season.cupRoster',
            widgetOptions: {
              matchId: firstMatchIdInTournament,
              seasonId: getBetRadarSeasonId(srSeasonId),
              utid: getBetRadarTournamentId(srTournamentId),
              disableWidgetHeader: true,
            },
          },
        }
      : null,
);

const selectIsEventCenterWidgetAvailable = createSelector(
  [
    selectEntitiesMatchesEntitySportId,
    selectEntitiesMatchesEntityCategoryId,
    selectEntitiesMatchesEntityImgEventId,
    selectEntitiesMatchesEntityImgFightId,
  ],
  (sportId, categoryId, imgEventId, imgFightId) => {
    const config = getEventCenterConfig(sportId, categoryId);

    return (
      !!config &&
      ((sportId === SportIds.MMA && categoryId === CategoryIds.MMA_UFC && !!imgFightId && !!imgEventId) ||
        (sportId === SportIds.GOLF && !!imgEventId))
    );
  },
);

const selectHiddenWidgetIds = createSelector(
  [selectEntitiesMatchesEntityTournamentId, selectIsMatchEnded],
  (matchTournamentId, isMatchEnded) => {
    const hiddenWidgetTournamentIds = [
      TournamentIds.SOCCER_BUNDESLIGA,
      TournamentIds.SOCCER_CHAMPIONS_LEAGUE,
      TournamentIds.SOCCER_EUROPA_LEAGUE,
      TournamentIds.SOCCER_LA_LIGA,
      TournamentIds.SOCCER_LIGUE_1_UBER_EATS,
      TournamentIds.SOCCER_PREMIER_LEAGUE,
      TournamentIds.SOCCER_SERIE_A,
      TournamentIds.SOCCER_MAJOR_LEAGUE_SOCCER,
      TournamentIds.SOCCER_LIGA_MX_APERTURA,
      TournamentIds.SOCCER_EREDIVISIE,
      TournamentIds.SOCCER_LIGA_PORTURGAL,
      TournamentIds.SOCCER_INT_FRIENDLY_GAMES,
      TournamentIds.SOCCER_MLS_PLAYOFFS,
      TournamentIds.SOCCER_LIGA_MX,
      TournamentIds.SOCCER_LIGA_MX_FINALS,
      TournamentIds.SOCCER_LIGA_MX_FINALS_BIS,
    ];
    const shouldHideBRWidget =
      !!hiddenWidgetTournamentIds.find((tournamentId) => matchTournamentId === tournamentId) && isMatchEnded;

    if (shouldHideBRWidget) {
      return ['pitch', 'statistics'];
    }

    return [];
  },
);

const selectWidgetWarnings: Selector<Array<WidgetWarningType>> = createSelector(
  [selectEntitiesMatchesEntityTournamentId, selectIsMatchLive],
  (matchTournamentId, isMatchLive) => {
    const widgetTournamentIdsWithWarnings = [
      TournamentIds.SOCCER_BUNDESLIGA,
      TournamentIds.SOCCER_CHAMPIONS_LEAGUE,
      TournamentIds.SOCCER_EUROPA_LEAGUE,
      TournamentIds.SOCCER_LA_LIGA,
      TournamentIds.SOCCER_LIGUE_1_UBER_EATS,
      TournamentIds.SOCCER_PREMIER_LEAGUE,
      TournamentIds.SOCCER_SERIE_A,
      TournamentIds.SOCCER_MAJOR_LEAGUE_SOCCER,
      TournamentIds.SOCCER_LIGA_MX_APERTURA,
      TournamentIds.SOCCER_EREDIVISIE,
      TournamentIds.SOCCER_LIGA_PORTURGAL,
      TournamentIds.SOCCER_INT_FRIENDLY_GAMES,
      TournamentIds.SOCCER_MLS_PLAYOFFS,
      TournamentIds.SOCCER_LIGA_MX,
      TournamentIds.SOCCER_LIGA_MX_FINALS,
      TournamentIds.SOCCER_LIGA_MX_FINALS_BIS,
    ];
    const shouldHaveWarning =
      !!widgetTournamentIdsWithWarnings.find((tournamentId) => matchTournamentId === tournamentId) && isMatchLive;

    if (shouldHaveWarning) {
      return [
        {
          widgetId: 'pitch',
          message: __('!desc:BROptaWidgetWarning'),
        },
        {
          widgetId: 'statistics',
          message: __('!desc:BROptaWidgetWarning'),
        },
      ];
    }

    return [];
  },
);

const selectMatchToolbarProps = createSelector(
  [
    getMatchIdFromProps,
    selectEntitiesMatchesEntitySportId,
    selectEntitiesMatchesEntityCategoryId,
    selectUserConnected,
    selectUserConnecting,
    selectUserEmbeddedNotificationTarget,
    selectMatchHasStream,
    selectMatchHasHighlights,
    selectEntitiesMatchesEntityHighlights,
    selectMatchHasTracker,
    selectUserJwt,
    selectEntitiesMatchesEntityStream,
    selectRuntimeIsStreamHighlightsEnabled,
    selectOptions,
    selectDefaultTrackerTab,
    selectTrackerName,
    selectShowWidget,
    selectLiveTableProps,
    selectCupRosterProps,
    selectDisplayCupRoster,
    selectDisplayLiveTable,
    selectMatchViewMode,
    selectEntitiesMatchesEntityIsPrivateScore,
    selectIsMatchSportLineUpCompatible,
    selectEntitiesMatchesEntityPeriodId,
    selectIsMatchLive,
    selectIsEventCenterWidgetAvailable,
    selectHiddenWidgetIds,
    selectWidgetWarnings,
  ],
  (
    matchId,
    sportId,
    categoryId,
    connected,
    isConnecting,
    notificationTarget,
    hasStream,
    hasHighlights,
    highlights,
    hasTracker,
    jwt,
    streamData,
    isStreamHighlightsEnabled,
    options,
    defaultTrackerTab,
    trackerName,
    showWidget,
    liveTableProps,
    cupRosterProps,
    displayCupRoster,
    displayLiveTable,
    matchViewMode,
    isPrivateScore,
    isMatchSportLineUpCompatible,
    periodId,
    isLive,
    isEventCenterWidgetAvailable,
    hiddenWidgetIds,
    widgetWarnings,
  ) => ({
    matchId,
    sportId,
    categoryId,
    connected,
    isConnecting,
    notificationTarget,
    hasStream,
    hasHighlights,
    highlights,
    hasTracker,
    jwt,
    streamData,
    isStreamHighlightsEnabled,
    options,
    defaultTrackerTab,
    trackerName,
    showWidget,
    liveTableProps,
    cupRosterProps,
    displayCupRoster,
    displayLiveTable,
    alreadyOpen: false,
    matchViewMode,
    isPrivateScore: MatchUtils.isPrivateScore(sportId, isPrivateScore),
    isMatchSportLineUpCompatible,
    periodId,
    isLive,
    isEventCenterWidgetAvailable,
    hiddenWidgetIds,
    widgetWarnings,
  }),
);

export default selectMatchToolbarProps;
